import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { HelpDialog } from '@alwindows/shared'
import { RouteAnnouncer } from '@alwindows/shared/a11y'
import { postScreenEmailHelp } from '@alwindows/shared/api/screens'
import { useIntroVisibility } from './IntroVisibility'
import { useBreadcrumbValue } from './Breadcrumb'
import { useQuote } from '../quote/QuoteContext'
import { ROUTE_EDIT, ROUTE_PAYMENT, ROUTE_SELECT_SCREENS, ROUTE_SUBMIT, ROUTE_SUMMARY } from '../routes'

const STEPS: Array<{ path: string; label: string }> = [
  { path: '/', label: 'Address' },
  { path: ROUTE_SELECT_SCREENS, label: 'Select screens' },
  { path: ROUTE_SUMMARY, label: 'Summary' },
  { path: ROUTE_SUBMIT, label: 'Submit' },
]

/**
 * Index into STEPS of the step the user is currently on. `/screen/edit/...`
 * isn't a step of its own — it's the configurator opened FROM Select
 * screens — so it counts as that step for the purposes of "which earlier
 * steps are complete". -1 when the path belongs to no step (e.g. payment).
 */
function currentStepIndex(pathname: string): number {
  const idx = STEPS.findIndex((s) => matchesStep(pathname, s.path))
  if (idx !== -1) return idx
  if (pathname.startsWith(ROUTE_EDIT)) return STEPS.findIndex((s) => s.path === ROUTE_SELECT_SCREENS)
  return -1
}

function describePath(pathname: string): string | null {
  const step = STEPS.find((s) => matchesStep(pathname, s.path))
  if (step) return `${step.label} — step ${STEPS.indexOf(step) + 1} of ${STEPS.length}`
  if (pathname.startsWith(ROUTE_EDIT)) return 'Configure screen'
  if (pathname.startsWith(ROUTE_PAYMENT)) return 'Payment'
  return null
}

export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const { introVisible } = useIntroVisibility()
  const breadcrumb = useBreadcrumbValue()
  const { quote } = useQuote()
  // Steps BEFORE the current one are links so the customer can go back and
  // change something; the current step and everything ahead of it are plain
  // text — forward movement only happens through each page's own CTA, so
  // nobody can skip a step. Once the quote has been saved (refNo set) the
  // back links go too: the flow is finished and editing a submitted quote
  // would only mislead — "Start over" on the confirmation is the way out.
  const currentIdx = currentStepIndex(pathname)
  const canGoBack = !quote.refNo
  // The progress step list is hidden while the marketing intro is on
  // screen (ScreenHome's first sub-state at `/`). The moment the user
  // clicks "Get started" they enter step 1 (Address) — still on `/`, but
  // now configurator chrome is appropriate. Combining the pathname check
  // with `introVisible` guards against a brief flash of the step list
  // during the first render at `/` before ScreenHome's effect runs.
  // The payment page hides it too: customers land there from the approval
  // email days later — it's a standalone task, not a step in the quote
  // flow (and "step 4 of 4" chrome would imply they're mid-quote).
  const showSteps = (pathname !== '/' || !introVisible) && !pathname.startsWith(ROUTE_PAYMENT)
  // The marketing intro already carries its own "Get started" call to action;
  // the Help trigger competes with that and isn't relevant until the user is
  // actually in the flow. Hide it on the intro and surface it from step 1 on
  // (same condition as the progress chrome).
  const showHelp = pathname !== '/' || !introVisible
  return (
    <div className="al-sc align-center">
      <RouteAnnouncer describePath={describePath} />
      {/* Tiny header strip, same as spare-parts — currently just hosts the
          Help trigger (live-site parity: help popup at the top right).
          Posts to the screens mailer, /v1/screen/email/help. */}
      {showHelp ? (
        <div className="al-sc__topbar">
          <HelpDialog onSubmit={postScreenEmailHelp} />
        </div>
      ) : null}
      {/* The WP theme already provides its own page header — a "Screens
          quote" brand link inside the app reads as a duplicate. The
          RouteAnnouncer above still announces page transitions for AT
          users without a visible bar. */}
      {breadcrumb ? (
        // Visual style mirrors the spare-parts app's `.al-cart__back` so the
        // two configurators feel like the same product. The onClick is
        // page-owned (not URL navigation) — pages register the breadcrumb
        // via useBreadcrumb() and tear it down on unmount.
        <button type="button" className="al-sc__back m-bx5 xsmall" onClick={breadcrumb.onClick}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {breadcrumb.label}
        </button>
      ) : null}
      {showSteps ? (
        // Numbered-step indicator — the same visual language as the intro's
        // 1-2-3-4 graphic (badge + label + dashed connectors), minus the
        // icons. The CURRENT step's badge inverts: red border + red digit on
        // transparent; the rest keep the solid red chip. Completed steps
        // render as Links (back navigation only); `aria-current="step"`
        // carries the active semantics for AT.
        <ol className="al-sc__steps" aria-label="Progress">
          {STEPS.map((step, i) => {
            const active = matchesStep(pathname, step.path)
            const completed = canGoBack && i < currentIdx
            const inner = (
              <>
                <span className="al-sc__step-num" aria-hidden="true">
                  {i + 1}
                </span>
                <p className="al-sc__step-label uppercase bold no-m">{step.label}</p>
              </>
            )
            return (
              <li
                key={step.path}
                className={`al-sc__step ${active ? 'is-active' : ''} ${completed ? 'is-complete' : ''}`}
                aria-current={active ? 'step' : undefined}
              >
                {completed ? (
                  // Going back to Address should land on the address form,
                  // not the marketing intro that `/` normally opens on —
                  // ScreenHome reads this flag from router state.
                  <Link
                    to={step.path}
                    state={step.path === '/' ? { skipIntro: true } : undefined}
                    className="al-sc__step-link"
                  >
                    {inner}
                  </Link>
                ) : (
                  inner
                )}
              </li>
            )
          })}
        </ol>
      ) : null}
      {/* Not a <main> — the WP theme already provides the main landmark. */}
      <div className="al-sc__main">{children}</div>
    </div>
  )
}

function matchesStep(pathname: string, stepPath: string): boolean {
  if (stepPath === '/') return pathname === '/'
  return pathname.startsWith(stepPath)
}
